import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCurrentLanguage, useLanguageSwitcher } from '../hooks/useTranslation'
import { localizedPathname } from '../lib/localizedPath'

const LanguageSwitcher = () => {
  const currentLanguage = useCurrentLanguage()
  const changeLanguage = useLanguageSwitcher()
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const listboxId = 'language-switcher-listbox'

  const languages = [
    { value: 'en', label: '🇺🇸 EN' },
    { value: 'pt', label: '🇧🇷 PT' },
  ]

  const currentLanguageData = languages.find((lang) => lang.value === currentLanguage)

  const handleLanguageChange = (languageValue) => {
    changeLanguage(languageValue)
    navigate(localizedPathname(location.pathname, languageValue))
    setIsOpen(false)
  }

  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-12 w-12 touch-manipulation items-center justify-center rounded-full border border-gray-300 bg-white px-3 py-3 text-base font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-secondary-600 focus:border-secondary-600 md:h-auto md:w-auto md:min-w-[100px] md:justify-between md:rounded-md md:px-3 md:py-2.5 md:pr-6"
        aria-label="Language selection"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
      >
        <span className="hidden md:inline">{currentLanguageData?.label || '🇺🇸 EN'}</span>
        <span className="text-lg md:hidden">{currentLanguageData?.label.split(' ')[0] || '🇺🇸'}</span>
        <svg
          className={`hidden w-4 h-4 text-gray-400 transition-transform duration-200 md:block ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Choose language"
          className="absolute top-full right-0 z-50 mt-1 min-w-[120px] rounded-lg border border-gray-300 bg-white shadow-lg md:left-0 md:right-0 md:min-w-[100px]"
        >
          {languages.map((language) => (
            <button
              key={language.value}
              type="button"
              role="option"
              aria-selected={currentLanguage === language.value}
              onClick={() => handleLanguageChange(language.value)}
              className={`w-full touch-manipulation px-4 py-3 text-left text-base font-semibold hover:bg-gray-50 active:bg-gray-100 first:rounded-t-lg last:rounded-b-lg md:px-3 md:py-2.5 ${
                currentLanguage === language.value
                  ? 'bg-secondary-50 text-secondary-700'
                  : 'text-gray-700'
              }`}
            >
              {language.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default LanguageSwitcher
